// İSG uzmanı portalı — bir SAHAYA bağlı güvenlik oturumu (S32b `docs/reviews/
// S32b-gate-desk-relayout.md` Option A, §2.4 kural 9). `SecurityDeskPage`'de
// `key={site.uuid}` ile monte edilir — bir saha değişimi bu bileşeni YAPISAL olarak REMOUNT
// eder (temizlik kod İLE değil, React'ın kendisiyle): açık kapı oturumu, sahnedeki karar, TC
// taslağı, kuyruk — HEPSİ sıfırlanır, çünkü hiçbiri artık VAR OLMAZ. "Bir karar asla yanlış bir
// sahayla yeniden etiketlenmez" garantisi böylece yapısal olur, elle `setState([])` çağrılarıyla
// DEĞİL (S32 review #1 ile AYNI disiplin, eskiden `TcLookupTab`'a uygulanan `key` deseninin
// TÜM oturuma genişletilmiş hâli).
//
// **S32e Part Two (`docs/reviews/S32e-evidence-and-layout.md` §4, Option A [O]) — ızgara
// `[Karar | Giriş]` üstte + tam-genişlik `Bugün` ALTTA.** Eski 1/2·1/2 ızgara (`Bugün`+`Giriş`
// yan yana, `VerdictPanel` tam genişlikte HER ZAMAN üstte) `VerdictCard`'ı yarım-genişlik için
// tasarlanmış ölçüsünde sayfa genişliğinde render ediyordu (§3.1 teşhisi: panelin ~%40'ı
// YAPISAL boş) VE `Bugün` tablosunu 1280+kenar çubuğunda ~210px TAŞACAK kadar aç bırakıyordu
// (§3.2, G4). Artık `Karar` (`xl:grid-cols-[3fr_2fr]`'in 3fr'i) ile `Giriş` (2fr) AYNI satırda;
// `Bugün` bu ızgaranın DIŞINDA, kendi satırında tam genişlik.
//
// **DOM sırası artık `[Karar][Giriş][Bugün]` — `xl:order-first` YOK, GEREKMİYOR (S32d §7.4'ün
// takas ettiği DOM-sırası=görsel-sıra ÇELİŞKİSİ ÇÖZÜLDÜ).** `xl`'de ızgara solda `Karar`ı sağda
// `Giriş`i verir (soldan-sağa OKUMA sırası = DOM sırası); `xl`'İN ALTINDA `grid-cols-1` üç öğeyi
// DOM sırasıyla YIĞAR — S32b §8.2'nin ÖNCE tercih ettiği **Karar → Giriş → Bugün** sırasına
// geri döner. Bu aynı zamanda **G2'yi ÇÖZER**: T.C. Kimlik alanına giden klavye yolu artık
// `Bugün`ün ~250 satır düğmesinin İÇİNDEN GEÇMİYOR — `Giriş` DOM'da `Bugün`'ün ÖNÜNDE.
//
// **Her okutma/TC sonucunda gün listesi geçersiz kılınır (D3 fix) — YALNIZ `Allowed`
// değil.** Reddedilenler artık bu listenin satırlarıdır (§29.5 S32b); eski `anyAllowed` koşulu
// bir reddi sonsuza dek görünmez BIRAKIRDI.

import type { EntryVerifyResultDto, GateScanDto, TcLookupResultDto } from '@isgportal/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { securityKeys } from '../../lib/securityKeys';
import { useGateCode } from '../../lib/useGateCode';
import { useSecurityDayEntries } from '../../lib/useSecurityDayEntries';
import { useVerdictStage } from '../../lib/useVerdictStage';
import { DayListTab } from './DayListTab';
import { EntryPanel } from './EntryPanel';
import { VerdictPanel } from './VerdictPanel';

export interface SecuritySiteSessionProps {
  site: { uuid: string; name: string };
  /** Yapışkan bağlam çubuğunun bağlantı noktası bunu okur (S32 §8.8 — "driven by `navigator.
   *  onLine` plus the outcome of the last request"); kaynak burada, gösterim yukarıda. */
  onConnectivityChange: (state: { gateSessionOpen: boolean; connectionOk: boolean }) => void;
}

export function SecuritySiteSession({ site, onConnectivityChange }: SecuritySiteSessionProps) {
  const queryClient = useQueryClient();
  const [gateSessionOpen, setGateSessionOpen] = useState(false);

  const dayEntries = useSecurityDayEntries(site.uuid);
  const verdictStage = useVerdictStage();

  const invalidateDay = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: securityKeys.entriesForDate(site.uuid, 'today'),
    });
  }, [queryClient, site.uuid]);

  const handleScansDiscovered = useCallback(
    (scans: GateScanDto[]) => {
      if (scans.length === 0) {
        return;
      }
      verdictStage.pushGateScans(scans);
      invalidateDay();
    },
    [verdictStage.pushGateScans, invalidateDay],
  );

  const gate = useGateCode(site.uuid, gateSessionOpen, handleScansDiscovered);

  useEffect(() => {
    onConnectivityChange({ gateSessionOpen, connectionOk: gate.connectionOk });
  }, [gateSessionOpen, gate.connectionOk, onConnectivityChange]);

  function handleTcResult(result: TcLookupResultDto) {
    verdictStage.pushTcResult(result);
    invalidateDay();
  }

  function handleTcNetworkError() {
    verdictStage.pushTcError();
  }

  // Akış A (`ScanTab`) — personel QR doğrulaması. Başarılı doğrulama sunucuda bir geçiş logu
  // yazar (`Method=PersonnelQr`, §29.5) → gün listesi geçersiz kılınır (TC/okutma ile AYNI).
  function handleVerifyResult(result: EntryVerifyResultDto) {
    verdictStage.pushVerifyResult(result);
    invalidateDay();
  }

  // Ağ/servis hatasında sunucuda bir şey yazılmadı → gün listesi geçersiz kılınmaz; nötr
  // "ağ hatası" kartı TC yoluyla AYNI (guard zaten aktif bir eylem yaptı, sahneyi keser).
  function handleVerifyNetworkError() {
    verdictStage.pushTcError();
  }

  // `security:gate-result` bilinen 403'ü — `dayEntries` erken (sayfa açılır açılmaz) yoklar,
  // `gate.panel.kind==='forbidden'` yalnız oturum başladıktan SONRA bilinir. İkisinin BİRLEŞİMİ
  // (S32b §5 kural 2) `Giriş` panelinin varsayılan alt-sekmesini VE `QR` sekmesinin içeriğini
  // yönlendirir.
  const gateForbidden = dayEntries.forbidden || gate.panel.kind === 'forbidden';

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[3fr_2fr]">
        <VerdictPanel
          stage={verdictStage.stage}
          queuedCount={verdictStage.queuedCount}
          nextIsDenial={verdictStage.nextIsDenial}
          selectedSiteName={site.name}
          onAcknowledge={verdictStage.acknowledge}
        />
        <EntryPanel
          siteUuid={site.uuid}
          gate={gate}
          gateSessionOpen={gateSessionOpen}
          onStartSession={() => setGateSessionOpen(true)}
          onStopSession={() => setGateSessionOpen(false)}
          forbidden={gateForbidden}
          onTcResult={handleTcResult}
          onTcNetworkError={handleTcNetworkError}
          onVerifyResult={handleVerifyResult}
          onVerifyNetworkError={handleVerifyNetworkError}
          // S32d owner talebi — bir okutma/sorgu SAHNEYE çıktığında kapı QR'ı ekrandan kalkar,
          // `Kapat` ile geri döner (`GateStage.tsx`'in dosya başı notunda TAM gerekçe + sahibe
          // AÇIKÇA bayraklanan gerilim). Oturumun/dönmenin KENDİSİ durmaz — yalnız GÖRÜNÜRLÜK.
          verdictPending={verdictStage.stage != null}
        />
      </div>
      <DayListTab entries={dayEntries} />
    </div>
  );
}
